import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '@/services';

// ============================================================================
// Types
// ============================================================================

export type AutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error' | 'conflict';

export interface AutoSaveConflict {
    serverVersion: number;
}

export interface UseAutoSaveOptions<T> {
    /** Debounce delay in ms. Default: 1500 */
    debounceMs?: number;
    /** Max retry attempts on network errors (not on 409/403). Default: 3 */
    maxRetries?: number;
    /** Base delay for exponential backoff in ms. Default: 1000 */
    retryBaseDelayMs?: number;
    /** Returns the current state to save */
    getState: () => T;
    /** Performs the save. Must return the new version from the server. */
    saveFn: (state: T, version: number) => Promise<{ version: number }>;
    /** Called on 409 conflict */
    onConflict?: (conflict: AutoSaveConflict) => void;
    /** Called on unrecoverable error (retries exhausted) */
    onError?: (error: Error) => void;
}

export interface UseAutoSaveResult {
    status: AutoSaveStatus;
    /** Schedule a debounced save */
    markDirty: () => void;
    /** Bypass debounce and save immediately */
    saveNow: () => void;
    /** Set the current version (call after initial load from server) */
    setVersion: (version: number) => void;
    conflict: AutoSaveConflict | null;
    dismissConflict: () => void;
    lastSavedAt: Date | null;
    retryCount: number;
}

/** The status/actions surface a persistence hook exposes to save-status UI. */
export type PersistenceState = Pick<
    UseAutoSaveResult,
    'status' | 'saveNow' | 'conflict' | 'dismissConflict' | 'lastSavedAt' | 'retryCount'
> & {
    /** Non-null if restoring state from the server failed (non-404). */
    restoreError: Error | null;
};

/** Worst-first precedence used to pick the aggregate status. */
const STATUS_SEVERITY: readonly AutoSaveStatus[] = [
    'conflict',
    'error',
    'saving',
    'pending',
    'saved',
    'idle',
];

/**
 * Merge several section persistence states into a single one so the UI can
 * show one save-status indicator for multiple auto-saved sections.
 */
export function mergePersistenceStates(states: PersistenceState[]): PersistenceState {
    const status = STATUS_SEVERITY.find((s) => states.some((state) => state.status === s)) ?? 'idle';

    let lastSavedAt: Date | null = null;
    for (const state of states) {
        if (state.lastSavedAt && (!lastSavedAt || state.lastSavedAt > lastSavedAt)) {
            lastSavedAt = state.lastSavedAt;
        }
    }

    return {
        status,
        lastSavedAt,
        retryCount: Math.max(0, ...states.map((state) => state.retryCount)),
        conflict: states.find((state) => state.conflict)?.conflict ?? null,
        restoreError: states.find((state) => state.restoreError)?.restoreError ?? null,
        // Only force-save sections with something to save — saving an untouched
        // (possibly never-initialized) section would persist empty state.
        saveNow: () => {
            states
                .filter((state) => state.status === 'pending' || state.status === 'error')
                .forEach((state) => state.saveNow());
        },
        // Dismissing resets a section to 'idle', so only touch conflicted
        // sections — others would lose their real status (e.g. 'pending').
        dismissConflict: () => {
            states.filter((state) => state.status === 'conflict').forEach((state) => state.dismissConflict());
        },
    };
}

// ============================================================================
// Hook
// ============================================================================

export function useAutoSave<T>(options: UseAutoSaveOptions<T>): UseAutoSaveResult {
    const { debounceMs = 1500, maxRetries = 3, retryBaseDelayMs = 1000 } = options;

    const [status, setStatus] = useState<AutoSaveStatus>('idle');
    const [conflict, setConflict] = useState<AutoSaveConflict | null>(null);
    const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
    const [retryCount, setRetryCount] = useState(0);

    // Use refs for values accessed in async callbacks to avoid stale closures
    const versionRef = useRef(0);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isSavingRef = useRef(false);
    const pendingAfterSaveRef = useRef(false);
    const mountedRef = useRef(true);
    const conflictRef = useRef(false);
    const optionsRef = useRef(options);
    optionsRef.current = options;

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        };
    }, []);

    const executeSave = useCallback(async () => {
        if (isSavingRef.current) {
            pendingAfterSaveRef.current = true;
            return;
        }

        isSavingRef.current = true;
        if (mountedRef.current) setStatus('saving');

        const currentState = optionsRef.current.getState();
        const currentVersion = versionRef.current;

        let attempt = 0;

        const attemptSave = async (): Promise<void> => {
            try {
                const result = await optionsRef.current.saveFn(currentState, currentVersion);

                if (!mountedRef.current) return;

                versionRef.current = result.version;
                setRetryCount(0);
                setStatus('saved');
                setLastSavedAt(new Date());

                isSavingRef.current = false;
                if (pendingAfterSaveRef.current) {
                    pendingAfterSaveRef.current = false;
                    scheduleSave();
                }
            } catch (error) {
                if (!mountedRef.current) {
                    isSavingRef.current = false;
                    return;
                }

                // 409 Conflict — do NOT retry
                if (error instanceof ApiError && error.status === 409) {
                    isSavingRef.current = false;
                    const serverVersion =
                        (error.body?.details as { currentVersion?: number })?.currentVersion ?? -1;
                    const conflictData: AutoSaveConflict = { serverVersion };
                    conflictRef.current = true;
                    setStatus('conflict');
                    setConflict(conflictData);
                    optionsRef.current.onConflict?.(conflictData);
                    return;
                }

                // 403 Forbidden — do NOT retry: a refusal (read-only
                // subscription, missing attestation) is a decision, not a blip,
                // and re-sending the same request three times cannot change it.
                // Fail straight to onError so the refusal is SURFACED — a
                // read-only clinician's in-progress edits must never be dropped
                // silently (ESCLOUD-288).
                if (error instanceof ApiError && error.status === 403) {
                    isSavingRef.current = false;
                    setStatus('error');
                    optionsRef.current.onError?.(error);
                    return;
                }

                // Network/server error — retry with exponential backoff
                attempt++;
                if (attempt <= maxRetries) {
                    setRetryCount(attempt);
                    const delay = retryBaseDelayMs * Math.pow(2, attempt - 1);
                    await new Promise((resolve) => setTimeout(resolve, delay));
                    if (mountedRef.current) {
                        await attemptSave();
                    } else {
                        isSavingRef.current = false;
                    }
                } else {
                    isSavingRef.current = false;
                    const finalError = error instanceof Error ? error : new Error('Auto-save failed');
                    setStatus('error');
                    optionsRef.current.onError?.(finalError);
                }
            }
        };

        await attemptSave();
    }, [maxRetries, retryBaseDelayMs]);

    const scheduleSave = useCallback(() => {
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        if (conflictRef.current) return;

        setStatus('pending');
        debounceTimerRef.current = setTimeout(() => {
            debounceTimerRef.current = null;
            executeSave();
        }, debounceMs);
    }, [debounceMs, executeSave]);

    const markDirty = useCallback(() => {
        scheduleSave();
    }, [scheduleSave]);

    const saveNow = useCallback(() => {
        if (debounceTimerRef.current) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
        executeSave();
    }, [executeSave]);

    const setVersion = useCallback((v: number) => {
        versionRef.current = v;
    }, []);

    const dismissConflict = useCallback(() => {
        conflictRef.current = false;
        setConflict(null);
        setStatus('idle');
    }, []);

    return {
        status,
        markDirty,
        saveNow,
        setVersion,
        conflict,
        dismissConflict,
        lastSavedAt,
        retryCount,
    };
}
